import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { UtilityService } from "../shared/service/utility.service";
import { BehaviorSubject, Observable, of } from "rxjs";
import { AppConfig } from "../shared/constants/app.config";
import { catchError, map, retry } from "rxjs/operators";
import * as uuid from "uuid";
import { AngularAppLogger } from '../angular-app-logger';

@Injectable()
export class MedicationsService {
    // BehaviorSubject for tracking medication payload update
    medsUpdatedPayload = new BehaviorSubject<any[]>([]);
    //Observable to subscribe to changes in medsPayload
    medications$ = this.medsUpdatedPayload.asObservable();

    constructor(
          readonly _http: HttpClient,
          readonly _utilityService: UtilityService,
          readonly _appLogger: AngularAppLogger
      ) {}

  /**
   * Method that updates
   * @param data
   */
  updateMedications(data: any[]) {
      this.medsUpdatedPayload.next(data);
    }

    // Method to get the current medications data
    getCurrentMedications() {
      return this.medsUpdatedPayload.getValue();
    }

    // Updated to bundle only the leafmeds inside the innerlevelmedications.
    generateLeafNodeBundles(selectedResources, psScope) {
      const leafNodeBundles = [];

      const allLeafMeds = selectedResources.flatMap(resource => resource.innerlevelMedications?.leaf
        ? Array.from(resource.innerlevelMedications.leaf.values()) : []).filter(med => med != null);

      if (allLeafMeds.length === 0) {
        return leafNodeBundles;
      }

      // Removing duplicates
      const uniqueLeafMeds = [
        ...new Map(allLeafMeds.map(med => [med.id, med])).values()
      ];

      const selectedLeafChunks = this._utilityService.chunk(uniqueLeafMeds, this._utilityService.chunkSize);

      for (const chunk of selectedLeafChunks) {
        const bundle = this.constructBundleEntries(chunk, psScope);
        bundle.entry.forEach(entry => this.replaceReference(entry.resource) );
        leafNodeBundles.push(bundle);
      }
      return leafNodeBundles;
    }

    // Fetch all non-null non-leaf meds from innerlevelMedications
    getNonLeafNodes(selectedResources) {
      return selectedResources.flatMap(resource => resource.innerlevelMedications?.nonLeaf
      ? Array.from(resource.innerlevelMedications.nonLeaf.values()) : []).filter(med => med != null);
    }

    generateMedicationReferenceBundles(selectedResources, lookup, psScope) {
        const bundles = [];

        // Remove duplicates from selectedResources
        const uniqueSelectedResources = [...new Map(selectedResources.map(item => [item.id, item])).values()];

        uniqueSelectedResources.map((resource: { ingredient?: any }) => {
            const { ingredient } = resource;
            ingredient?.map((ingredient: { itemReference?: any }) => {
                const itemReference = ingredient?.itemReference;
                if (itemReference) {
                    const medId = itemReference.reference.split("/").pop();
                    itemReference.reference = "Medication/" + lookup[medId];
                }
            });
        });

        const chunks = this._utilityService.chunk(uniqueSelectedResources, this._utilityService.chunkSize);

        chunks.forEach(selectedMedicationChunkResponse => {
            const bundleEntry = this.constructBundleEntries(selectedMedicationChunkResponse, psScope);
            bundleEntry.entry.forEach(entry => {
                this.replaceReference(entry.resource);
            });
            bundles.push(bundleEntry);
        });

        return bundles;
    }

    generateMedicationReqBundles(selectedResources, lookup, psScope) {
        const bundles = [];

        selectedResources.map(resource => {
            const medicationReference = resource?.medicationReference;
            if (medicationReference) {
                const id = medicationReference.reference.split("/").pop();
                medicationReference.reference = "Medication/" + lookup[id];
            }
        });

        const chunks = this._utilityService.chunk(selectedResources, this._utilityService.chunkSize);

        chunks.forEach(selectedMedicationChunkResponse => {
            const bundleEntry = this.constructBundleEntries(selectedMedicationChunkResponse, psScope);

            bundleEntry.entry.forEach(entry => {
                this.replaceReference(entry.resource);
            });

            bundles.push(bundleEntry);
        });

        return bundles;
    }

    constructBundleEntries(selectedChunkResource, psScope) {
        const innerentries = [];

        selectedChunkResource.forEach(selectedChunkElement => {
            innerentries.push(this.constructMedicationBundleEntry(selectedChunkElement, psScope));
        });
        return {
            resourceType: "Bundle",
            type: "transaction",
            entry: innerentries,
        };
    }

    constructMedicationBundleEntry(resource, scope) {
        const bundleEntry = {
            fullUrl: "urn:uuid:" + uuid.v4(),
            resource: {
                ...resource,
                meta: {
                    security: [
                        {
                            system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
                            code: scope,
                        },
                    ],
                },
                identifier: [
                    {
                        use: "official",
                        system: AppConfig.FHIR_LOGICALID_NAMESPACE,
                        value: `${localStorage.getItem("iss")}/${resource.resourceType}/${resource.id}`,
                    },
                ],
            },
            request: {
                method: "POST",
                url: resource.resourceType,
                ifNoneExist:
                    "identifier=" +
                    localStorage.getItem("iss") +
                    "/" +
                    resource.resourceType +
                    "/" +
                    resource.id,
            },
        };
        return bundleEntry;
    }

    postBundle(bundle) {
        return this._http
            .post(localStorage.getItem("cibmtr_url") + "Bundle", bundle)
            .pipe(
                map(response => response),
                retry(1),
                catchError(error => {
                    throw error;
                })
            )
            .toPromise();
    }

    replaceReference(object) {
        delete object.id;
        Object.keys(object).forEach(key => {
            if (key !== "subject" && key !== "medicationReference" && key !== "itemReference") {
                if (key === "reference") {
                    object[key] = `${localStorage.getItem("iss")}/${object[key]}`;
                }
                if (typeof object[key] === "object" && object[key] !== null) {
                    this.replaceReference(object[key]);
                }
            }
        });
        // subject references will be updated with CIBMTR local Id
        if (object.subject) {
            object.subject.reference = `Patient/${localStorage.getItem("patient_local_id")}`;
        }
    }

    getCibmtrMedicationRequests(psScope: string): Observable<any> {
        const cibmtrUrl = localStorage.getItem("cibmtr_url");
        const medReq = "MedicationRequest";
        const subRef = "?subject=Patient/" + localStorage.getItem("patient_local_id");
        const secSpe = `&_security=${psScope}&_total=accurate&_count=500`;

        const url = cibmtrUrl + medReq + subRef + secSpe;
        return this._http.get(url);
    }
}
